import { DateTime } from 'luxon';
import Batch from 'smg/batch';
import * as models from 'smg/customer-api/models';
import { Orders } from 'smg/customer-api/orders';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { PageLoader } from '../account';
import { AccountReady } from '../account-session';
import { Relref } from '../hugo';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const b = new Batch();

    void b.exec();

    const pl: PageLoader<models.HyejooOrder> = new PageLoader<models.HyejooOrder>({
        get: () => Orders({
            sort: pl.sort(),
            max: pl.currentPageSize(),
            data: 'invoiceNumber,created,orderLineItems,orderProofs,orderShippingOptions,orderPayments,total,paid,balance,totalSansTax,orderStatusID',
            p: pl.currentPage(),
            q: pl.search(),
        }).then(data => ({ rows: data.results, nextPage: data.nextPage })),
        draw: order => {
            const el = template.Clone('#order-template').firstElementChild as HTMLElement;

            const orderNumber = el.querySelector('#template-order-number') as HTMLAnchorElement;
            orderNumber.textContent = String(order.invoiceNumber);
            orderNumber.href = `${Relref('/orders')}track/?id=${order.id}`;

            const shippedTo = el.querySelector('#recipients-container') as HTMLElement;
            const shipmentRecipients: string[] = [];
            for (const orderShippingOption of order.orderShippingOptions) {
                if (!orderShippingOption.shippingOption) {
                    continue;
                }
                const shippingAddress = orderShippingOption.shippingOption.address;
                if (!shippingAddress) {
                    continue;
                }
                if (shippingAddress.fullName) {
                    shipmentRecipients.push(shippingAddress.fullName);
                }
            }
            for (const recipient of shipmentRecipients) {
                const pEl = document.createElement('p');
                pEl.style.marginBottom = '0';
                pEl.textContent = recipient;
                shippedTo.append(pEl);
            }

            const total = el.querySelector('#template-order-total') as HTMLElement;
            total.textContent = DollarFormat(order.total);

            const orderStatus = el.querySelector('#template-order-status') as HTMLElement;
            const orderShipments: models.OrderShippingOptionShipment[] = [];
            for (const shippingOption of order.orderShippingOptions) {
                if (shippingOption.orderShippingOptionShipments.length) {
                    for (const shipment of shippingOption.orderShippingOptionShipments) {
                        orderShipments.push(shipment);
                    }
                }

                let allDelivered = false;
                let hasShipments = false;
                let partiallyDelivered = false;
                let notAllDelivered = false;
                let someDelivered = false;
                for (const shipment of orderShipments) {
                    if (shipment.shipment) {
                        hasShipments = true;
                    }

                    if (!shipment.shipment?.deliveredAt.isValid) {
                        notAllDelivered = true;
                    } else {
                        someDelivered = true;
                    }
                }
                if (hasShipments && !notAllDelivered) {
                    allDelivered = true;
                }
                if (hasShipments && !allDelivered && someDelivered) {
                    partiallyDelivered = true;
                }

                switch (true) {
                case allDelivered:
                    orderStatus.textContent = 'Delivered';
                    orderStatus.style.color = 'green';
                    break;
                case partiallyDelivered:
                    orderStatus.textContent = 'Partially Delivered';
                    orderStatus.style.color = 'green';
                    break;
                case hasShipments:
                    orderStatus.textContent = 'Shipped';
                    orderStatus.style.color = 'green';
                    break;
                case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED:
                    orderStatus.textContent = 'In Production';
                    orderStatus.style.color = 'rgb(255, 205, 61)';
                    break;
                case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_UNCONFIRMED:
                    orderStatus.textContent = 'Preparing for Production';
                    orderStatus.style.color = 'rgb(255, 205, 61)';
                    break;
                case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_ORDER:
                    orderStatus.textContent = 'Order Received';
                    orderStatus.style.color = 'rgb(255, 205, 61)';
                    break;
                }
            }

            const firstChildEl = el.firstElementChild;
            if (firstChildEl) {
                firstChildEl.id = order.id;
                firstChildEl.classList.add('bg-target');
                firstChildEl.addEventListener('click', () => {
                    const initSummaryText = document.querySelector('#init-order-summary-text') as HTMLElement;
                    initSummaryText.classList.add('d-none');

                    const orderSummaryInfo = document.querySelector('#order-summary-info') as HTMLElement;
                    orderSummaryInfo.classList.remove('d-none');

                    firstChildEl.firstElementChild?.classList.add('bg-primary', 'bg-opacity-10');
                    const allBgTarget = Array.from(document.querySelectorAll('.bg-target'));
                    for (const bgTarget of allBgTarget) {
                        if (bgTarget.id !== firstChildEl.id) {
                            bgTarget.firstElementChild?.classList.remove('bg-primary', 'bg-opacity-10');
                        }
                    }
                    const latestProofEl = document.querySelector('#latest-order-proof') as HTMLElement;
                    if (!order.orderProofs.length) {
                        latestProofEl.classList.add('d-none');
                    } else {
                        let latestProof;
                        const sortedProofs = order.orderProofs.reverse();
                        for (const proof of sortedProofs) {
                            latestProof = proof;
                            break;
                        }
                        const latestProofImgEl = document.querySelector('#latest-order-proof-img') as HTMLImageElement;
                        if (latestProof?.proof?.main) {
                            if (latestProof.proof.thumbnail) {
                                latestProofImgEl.src = latestProof.proof.thumbnail;
                            }
                        }
                    }
                    const orderNumber = document.querySelector('#order-number') as HTMLElement;
                    orderNumber.textContent = String(order.invoiceNumber);

                    const orderDate = document.querySelector('#order-date') as HTMLElement;
                    if (order.created.isValid) {
                        orderDate.textContent = order.created.toLocaleString(DateTime.DATE_SHORT);
                    }

                    const orderPreviewStatus = document.querySelector('#order-status') as HTMLElement;
                    orderPreviewStatus.textContent = orderStatus.textContent;

                    const viewDetailsBtn = document.querySelector('#view-details-btn') as HTMLButtonElement;
                    viewDetailsBtn.classList.remove('d-none');
                    viewDetailsBtn.addEventListener('click', () => {
                        window.location.href = `${Relref('/order')}?id=${order.id}`;
                    });
                });
            }

            return el;
        },
    });

    pl.load();
}
